import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from '@/components/common/SelectField';
import UploadColumnCheck from '@/components/upload/UploadColumnCheck';
import { isTableFormat } from '@/utils/upload';
import { Upload, UploadStartRequest } from '@/types/upload';

interface UploadPreviewPanelProps {
  /** A waiting upload. Give the panel key={upload.id}, so it starts over for another upload. */
  upload: Upload;
  disabled: boolean;
  /** The start request the choices make, or null with why not yet; the dialog's footer holds the start button. */
  onChange: (request: UploadStartRequest | null, problem: string | null) => void;
}

/** The column check of one waiting upload: the sheet (or layer) picker, and the check of the chosen one. */
const UploadPreviewPanel: React.FC<UploadPreviewPanelProps> = ({ upload, disabled, onChange }) => {
  const { t } = useTranslation();
  const sheets = upload.preview?.sheets ?? [];
  const [sheetName, setSheetName] = useState(sheets[0]?.name ?? '');
  const sheet = sheets.find((s) => s.name === sheetName) ?? sheets[0];

  if (!sheet) return <p className="text-sm text-gn-danger">{t('uploads.no_preview')}</p>;
  return (
    <div className="space-y-5" data-testid="upload-check">
      <p className="text-sm text-gn-muted">{t('uploads.check_hint')}</p>
      {sheets.length > 1 && (
        <SelectField label={isTableFormat(upload) ? t('uploads.sheet') : t('uploadShp.layer')} value={sheet.name}
                     disabled={disabled} onChange={(e) => setSheetName(e.target.value)} className="sm:max-w-sm">
          {sheets.map((s) => (
            <option key={s.name} value={s.name}>
              {s.name}{s.rowCount != null ? ` (${t('uploads.rows', { count: s.rowCount })})` : ''}
              {s.geometryType ? ` · ${t(`uploads.geometry_${s.geometryType}`, { defaultValue: s.geometryType })}` : ''}
            </option>
          ))}
        </SelectField>
      )}
      <UploadColumnCheck key={`${upload.id}:${sheet.name}`} upload={upload} sheet={sheet} disabled={disabled} onChange={onChange} />
    </div>
  );
};

export default UploadPreviewPanel;
